import React from 'react';
import PropTypes from 'prop-types';
import { useQuizModal } from '@/Hooks/useQuizModal';

const Overlay = ({ onClose, children }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
    onClick={onClose ?? undefined}
  >
    <div
      className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden"
      onClick={onClose ? e => e.stopPropagation() : undefined}
    >
      {children}
    </div>
  </div>
);

Overlay.propTypes = {
  onClose: PropTypes.func,
  children: PropTypes.node.isRequired,
};

Overlay.defaultProps = {
  onClose: null,
};

const StepIndicator = ({ current, total }) => (
  <div className="flex gap-1.5 justify-center mb-4">
    {Array.from({ length: total }).map((_, i) => (
      <span
        key={i}
        className={`h-1.5 rounded-full transition-all ${
          i <= current ? 'bg-primary-500 w-6' : 'bg-neutral-200 w-3'
        }`}
      />
    ))}
  </div>
);

StepIndicator.propTypes = {
  current: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
};

const OptionButton = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
      selected
        ? 'border-primary-500 bg-primary-50 text-primary-700'
        : 'border-neutral-200 bg-white text-neutral-700 hover:border-primary-300'
    }`}
  >
    {label}
  </button>
);

OptionButton.propTypes = {
  label: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

function QuizModalInner({ quiz, step, currentIndex, currentQuestion, currentAnswer, hasAnswer, isTextQuestion, submitting, handleClose, handleStart, handleSelectAnswer, handleToggleCheckbox, handleNext }) {
  const totalQuestions = quiz?.questions?.length ?? 0;

  if (step === 'intro') {
    return (
      <Overlay>
        <div className="p-6 flex flex-col gap-4">
          <div>
            <p className="text-xs font-semibold text-primary-500 uppercase tracking-wide mb-1">
              {quiz.quizTypeLabel}
            </p>
            <h2 className="text-xl font-bold text-neutral-800 leading-snug">{quiz.title}</h2>
          </div>
          {quiz.generalInstructions && (
            <p className="text-sm text-neutral-500 leading-relaxed">{quiz.generalInstructions}</p>
          )}
          <p className="text-xs text-neutral-500">
            {totalQuestions} {totalQuestions === 1 ? 'pergunta' : 'perguntas'}
          </p>
          <button
            onClick={handleStart}
            className="mt-2 w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 rounded-xl transition-colors"
          >
            Começar
          </button>
        </div>
      </Overlay>
    );
  }

  if (step === 'done') {
    return (
      <Overlay onClose={handleClose} >
        <div className="p-6 flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-3xl">
            ✓
          </div>
          <h2 className="text-xl font-bold text-neutral-800">Obrigado pela participação!</h2>
          <p className="text-sm text-neutral-500">Suas respostas foram registradas com sucesso.</p>
          <button
            onClick={handleClose}
            className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 rounded-xl transition-colors"
          >
            Fechar
          </button>
        </div>
      </Overlay>
    );
  }

  if (!currentQuestion) return null;

  const isLast = currentIndex === totalQuestions - 1;

  return (
    <Overlay>
      <div className="p-6 flex flex-col gap-5">
        <div className="flex justify-end items-center">
          <span className="text-xs text-neutral-500 font-medium">
            {currentIndex + 1} / {totalQuestions}
          </span>
        </div>

        <StepIndicator current={currentIndex} total={totalQuestions} />

        <div>
          {currentQuestion.title && (
            <p className="text-xs font-semibold text-primary-500 uppercase tracking-wide mb-1">
              {currentQuestion.title}
            </p>
          )}
          <div className="flex items-start gap-2">
            <h3 className="text-base font-semibold text-neutral-800 leading-snug flex-1">
              {currentQuestion.question}
            </h3>
            {isTextQuestion && (
              <span className="shrink-0 text-xs text-neutral-400 font-normal mt-0.5">(Opcional)</span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {/* MULTIPLE_CHOICE (type 0) — checkbox */}
          {Number(currentQuestion.type) === 0 &&
            currentQuestion.options?.map((opt, i) => (
              <OptionButton
                key={i}
                label={opt}
                selected={Array.isArray(currentAnswer) && currentAnswer.includes(i)}
                onClick={() => handleToggleCheckbox(currentQuestion.id, i)}
              />
            ))}

          {/* SINGLE_CHOICE (type 1) e TRUE_FALSE (type 2) — radio */}
          {(Number(currentQuestion.type) === 1 || Number(currentQuestion.type) === 2) &&
            currentQuestion.options?.map((opt, i) => (
              <OptionButton
                key={i}
                label={opt}
                selected={currentAnswer === i}
                onClick={() => handleSelectAnswer(currentQuestion.id, i)}
              />
            ))}

          {/* TEXT (type 3) — textarea */}
          {Number(currentQuestion.type) === 3 && (
            <textarea
              className="w-full border-2 border-neutral-200 rounded-xl p-3 text-sm text-neutral-700 resize-none focus:outline-none focus:border-primary-500 transition-colors"
              rows={4}
              placeholder="Digite sua resposta..."
              value={currentAnswer ?? ''}
              onChange={e => handleSelectAnswer(currentQuestion.id, e.target.value)}
            />
          )}
        </div>

        <button
          onClick={handleNext}
          disabled={!hasAnswer || submitting}
          className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors"
        >
          {submitting ? 'Enviando...' : isLast ? 'Finalizar' : 'Avançar'}
        </button>
      </div>
    </Overlay>
  );
}

QuizModalInner.propTypes = {
  quiz: PropTypes.object.isRequired,
  step: PropTypes.oneOf(['intro', 'questions', 'done']).isRequired,
  currentIndex: PropTypes.number.isRequired,
  currentQuestion: PropTypes.object,
  currentAnswer: PropTypes.oneOfType([PropTypes.number, PropTypes.array, PropTypes.string]),
  hasAnswer: PropTypes.bool.isRequired,
  isTextQuestion: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleStart: PropTypes.func.isRequired,
  handleSelectAnswer: PropTypes.func.isRequired,
  handleToggleCheckbox: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
};

QuizModalInner.defaultProps = {
  currentQuestion: null,
  currentAnswer: undefined,
};

const QuizModal = () => {
  const state = useQuizModal();

  if (!state.isOpen || !state.quiz) return null;

  return <QuizModalInner {...state} />;
};

export default QuizModal;
